import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import config from "../config/config";

/**
 * Google reCAPTCHA v2 checkbox widget (DTS signup/login). Shows a visible
 * "I'm not a robot" challenge the user must actively solve before submitting —
 * unlike v3 (score-based, invisible), this always gives the user something to
 * interact with. Loaded dynamically, not via a global <script> tag in
 * index.html, so PPH (and every other page) never pays for loading Google's
 * script. No npm package added on purpose (matches this repo's "ask before
 * adding a dependency" convention) — reCAPTCHA's own script exposes
 * everything needed on window.grecaptcha.
 *
 * Requires a "v2 checkbox" type site key from
 * https://www.google.com/recaptcha/admin — a v3 key will fail to render here.
 *
 * reCAPTCHA tokens are single-use: Google invalidates a token the moment the
 * backend calls siteverify on it, regardless of whether the request that used
 * it succeeded. If a submit fails for any reason (wrong password, etc.), the
 * checkbox stays visually checked but its token is now dead — resubmitting
 * without re-solving fails with the same "please complete the CAPTCHA"
 * error even though the box still shows solved. Callers MUST call reset()
 * (via the forwarded ref) on every failed submit so the next attempt uses a
 * fresh token — see Login()/register() in pages/login.js and pages/signup.js.
 *
 * Props:
 *   onChange(token: string) — called with the solved token, or "" on expiry.
 * Ref:
 *   reset() — clears the widget back to unsolved, forcing a fresh token.
 */
const RECAPTCHA_SCRIPT_SRC = "https://www.google.com/recaptcha/api.js?render=explicit";

function loadRecaptchaScript() {
  if (window.grecaptcha && window.grecaptcha.render) return Promise.resolve();
  if (!window.__recaptchaV2ScriptPromise) {
    window.__recaptchaV2ScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = RECAPTCHA_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Failed to load reCAPTCHA script"));
      document.head.appendChild(script);
    });
  }
  return window.__recaptchaV2ScriptPromise;
}

const Captcha = forwardRef(function Captcha({ onChange, className = "" }, ref) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [failedToLoad, setFailedToLoad] = useState(false);
  const siteKey = config.RECAPTCHA_SITE_KEY;

  // The widget is rendered exactly once (below) and its callback closes over
  // whatever `onChange` was at that moment — a stale reference forever after,
  // since the render effect never reruns. Routing through a ref that's
  // updated every render means the callback always invokes the CURRENT
  // onChange (i.e. the current formValue/errors closure in the parent page),
  // not a snapshot from first mount.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  useImperativeHandle(ref, () => ({
    reset() {
      if (window.grecaptcha && widgetIdRef.current !== null) {
        window.grecaptcha.reset(widgetIdRef.current);
      }
    },
  }));

  useEffect(() => {
    if (!siteKey) return undefined;
    let cancelled = false;

    loadRecaptchaScript()
      .then(() => {
        if (cancelled || !containerRef.current) return;
        window.grecaptcha.ready(() => {
          if (cancelled || !containerRef.current || widgetIdRef.current !== null) return;
          widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
            sitekey: siteKey,
            callback: (token) => onChangeRef.current(token),
            "expired-callback": () => onChangeRef.current(""),
            "error-callback": () => onChangeRef.current(""),
          });
        });
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error("[recaptcha] widget failed to load:", err);
        if (!cancelled) setFailedToLoad(true);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey]);

  if (!siteKey) {
    return (
      <p className={`text-danger small mb-0 ${className}`}>
        CAPTCHA is not configured for this environment.
      </p>
    );
  }

  if (failedToLoad) {
    return (
      <p className={`text-danger small mb-0 ${className}`}>
        Could not load the CAPTCHA challenge. Please refresh and try again.
      </p>
    );
  }

  return <div ref={containerRef} className={className} />;
});

export default Captcha;
